/**
 * @author Nerius Ilmonas
 * @version 2025-08-22
 */

import { useEffect } from 'react';

import { fetchGetSellerWhitelabelling } from '@iwoca/lapi-client/iwocapay';
import { useQuery } from '@tanstack/react-query';

import { useGetSellerByHandle } from '../api/lending/lapiHooks';
import {
  useWhiteLabelTheme,
  type WhiteLabelTheme,
} from '../Buyer/store/WhiteLabelProvider';

/**
 * Retrieves seller white-labelling configuration.
 * @param sellerHandle The {@link https://git.iwoca.co.uk/iwoca/iwoca-django/-/blob/558575273b67e81d38f4e81ccde81797a6b4563b/iwocapay/models.py#L188 Seller.handle} to retrieve by.
 * @returns theme containing {@link WhiteLabelTheme} or error.
 * N.B. This hook can only be used within a WhiteLabelProvider.
 */
export function useSellerWhitelabelling(sellerHandle?: string): {
  theme?: WhiteLabelTheme;
  error: Error | null;
  showSellerLogo: boolean;
  isTheme: boolean;
  loadingWhitelabelling: boolean;
} {
  const { seller, loadingSeller } = useGetSellerByHandle(sellerHandle);

  const { data, error, isPending } = useQuery({
    queryKey: ['fetchGetSellerWhitelabelling', sellerHandle],
    queryFn: () =>
      fetchGetSellerWhitelabelling({ sellerHandle: sellerHandle! }),
    enabled: !!sellerHandle && !!seller?.pilots?.white_labelled,
  });

  const {
    setTheme,
    theme,
    showSellerLogo,
    isTheme,
    setIsThemeResolved,
    isThemeResolved,
  } = useWhiteLabelTheme();

  useEffect(() => {
    // Only set the theme if seller is active on white_labelled seller pilot.
    if (seller?.pilots?.white_labelled && data?.data && !error)
      setTheme(data.data);

    if (
      (!loadingSeller && !isPending) ||
      (!loadingSeller && !seller?.pilots?.white_labelled)
    )
      setIsThemeResolved(true);
  }, [
    seller?.pilots?.white_labelled,
    data?.data,
    error,
    isPending,
    loadingSeller,
    setTheme,
    setIsThemeResolved,
  ]);

  return {
    theme,
    error,
    showSellerLogo,
    isTheme,
    loadingWhitelabelling: !isThemeResolved,
  };
}
